import React, { useState, useRef } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { PLATFORM_NAME, PLATFORM_TAGLINE } from "../lib/config";
import BrandMark from "../components/BrandMark";
import AppFooter from "../components/AppFooter";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Eye, EyeOff } from "lucide-react";

const BG_URL =
  "https://images.unsplash.com/photo-1758691737584-a8f17fb34475?ixlib=rb-4.1.0&auto=format&fit=crop&w=1600&q=80";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Verrou anti double-submit : empêche un second appel à /auth/login si l'utilisateur
  // double-clique ou appuie sur Entrée + clique. Sans ce verrou, certaines requêtes
  // peuvent arriver dans le désordre côté navigateur et faire échouer la première.
  const submittingRef = useRef(false);

  if (user && user !== false) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    // On arme le verrou IMMÉDIATEMENT (avant le await blur+50ms) pour fermer
    // la fenêtre de vulnérabilité au double-clic rapide.
    submittingRef.current = true;
    // On lit directement les valeurs du DOM (et plus l'état React) pour contourner
    // le bug classique de l'auto-remplissage navigateur / gestionnaire de mots de
    // passe qui ne déclenche pas toujours onChange, laissant l'état React vide
    // alors que le champ visible est rempli.
    const form = e.currentTarget;
    const emailInput = form.elements.email;
    const passwordInput = form.elements.password;

    // CRITIQUE : Sur mobile (Android Chrome notamment) et avec certains gestionnaires
    // de mots de passe desktop, la valeur du champ password n'est pas encore
    // « finalisée » dans le DOM au moment du clic « Se connecter » si le focus
    // est encore sur le champ. On force un blur ici puis on laisse un tick au
    // navigateur pour flusher la valeur d'autofill avant de la lire.
    const active = document.activeElement;
    if (active === emailInput || active === passwordInput) {
      try { active.blur(); } catch { /* noop */ }
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    const emailValue = (emailInput?.value || email || "").trim();
    const passwordValue = passwordInput?.value || password || "";
    if (!emailValue || !passwordValue) {
      toast.error("Veuillez renseigner votre email et votre mot de passe");
      submittingRef.current = false;
      return;
    }
    // On synchronise l'état React au cas où l'autofill ait été silencieux
    if (emailValue !== email) setEmail(emailValue);
    if (passwordValue !== password) setPassword(passwordValue);
    setLoading(true);
    try {
      const res = await login(emailValue, passwordValue);
      if (res.success) {
        toast.success(`Bienvenue sur ${PLATFORM_NAME}`);
        navigate("/");
      } else {
        toast.error(res.error || "Échec de la connexion");
      }
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  const openForgot = () => {
    setForgotEmail(email);
    setForgotSent(false);
    setForgotOpen(true);
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error("Veuillez renseigner votre adresse email");
      return;
    }
    setForgotLoading(true);
    try {
      await api.post("/auth/password-reset-request", { email: forgotEmail.trim() });
      setForgotSent(true);
    } catch (err) {
      // Anti-énumération : on affiche un message uniforme même en cas d'erreur réseau
      // (la sécurité l'exige), mais on signale tout de même un souci visible si HTTP non 2xx.
      const status = err?.response?.status;
      if (status && status >= 500) {
        toast.error("Service indisponible — réessayez dans quelques minutes");
      } else {
        setForgotSent(true);
      }
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 bg-white relative">
      {/* Bande tricolore en haut (Bleu — Blanc — Rouge) — uniquement sur la partie blanche (panneau de gauche) */}
      <div
        className="absolute top-0 left-0 right-0 lg:right-1/2 h-1 flex z-10"
        aria-hidden="true"
        data-testid="login-tricolor-top"
      >
        <span className="flex-1 bg-[#0055A4]" />
        <span className="flex-1 bg-white" />
        <span className="flex-1 bg-[#EF4135]" />
      </div>

      {/* Left panel - form */}
      <div className="flex flex-col">
        {/* En-tête à 2 bandes centrées (logo+nom puis tagline) — visible desktop & mobile */}
        <div className="w-full border-b border-zinc-200" data-testid="login-brand-banner">
          <div className="px-4 py-5 lg:py-6 flex items-center justify-center border-b border-zinc-100">
            <BrandMark size="xl" monochrome uppercase />
          </div>
          <div className="px-4 py-2 bg-zinc-50/60 text-center">
            <span
              className="text-[11px] uppercase tracking-[0.18em] text-zinc-500"
              data-testid="login-tagline"
            >
              {PLATFORM_TAGLINE}
            </span>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-md px-6 py-12 lg:px-16 mx-auto">
            <div className="mb-10">
              <p className="label-uppercase mb-3">Espace sécurisé</p>
              <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-zinc-900 leading-tight" data-testid="login-title">
                Connectez-vous à votre compte
              </h1>
            </div>

            <form onSubmit={submit} className="space-y-5" data-testid="login-form">
              <div>
                <Label htmlFor="email" className="label-uppercase block mb-2">Adresse email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="username"
                  spellCheck={false}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 border-zinc-300 focus:border-blue-600 focus:ring-1 focus:ring-blue-600 rounded-sm"
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <Label htmlFor="password" className="label-uppercase block mb-2">Mot de passe</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 pr-11 border-zinc-300 focus:border-blue-600 focus:ring-1 focus:ring-blue-600 rounded-sm"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    title={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    className="absolute inset-y-0 right-0 px-3 flex items-center text-zinc-500 hover:text-zinc-800 transition-colors"
                    data-testid="login-password-toggle"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-sm font-medium"
                data-testid="login-submit-btn"
              >
                {loading ? "Connexion…" : "Se connecter"}
              </Button>
              <div className="text-center pt-1">
                <button
                  type="button"
                  onClick={openForgot}
                  className="text-xs text-zinc-500 hover:text-zinc-800 underline-offset-2 hover:underline transition-colors"
                  data-testid="forgot-password-link"
                >
                  Mot de passe oublié ?
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Right panel - visual */}
      <div className="hidden lg:block relative bg-zinc-100 overflow-hidden">
        <img
          src={BG_URL}
          alt={PLATFORM_NAME}
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Voile sombre en bas pour la lisibilité du texte */}
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/80 via-zinc-900/15 to-transparent" />
        {/* Accent tricolore discret en bas du panneau (Bleu — Blanc — Rouge) */}
        <div className="absolute bottom-0 left-0 right-0 h-1 flex" aria-hidden="true">
          <span className="flex-1 bg-[#0055A4]" />
          <span className="flex-1 bg-white" />
          <span className="flex-1 bg-[#EF4135]" />
        </div>
      </div>
      </div>
      <AppFooter />

      {/* Modale "Mot de passe oublié" — capture l'email puis crée une notification admin */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent data-testid="forgot-password-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mot de passe oublié ?</DialogTitle>
            <DialogDescription>
              Renseignez votre adresse email professionnelle. Votre administrateur sera notifié de votre demande et générera un nouveau mot de passe temporaire qu&apos;il vous transmettra.
            </DialogDescription>
          </DialogHeader>

          {forgotSent ? (
            <div className="space-y-3" data-testid="forgot-success-block">
              <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 text-sm text-emerald-900">
                <p className="font-semibold mb-1">Demande envoyée</p>
                <p>
                  Si cette adresse correspond à un compte actif, votre administrateur recevra
                  une notification et vous contactera dès que possible.
                </p>
              </div>
              <DialogFooter>
                <Button
                  onClick={() => setForgotOpen(false)}
                  data-testid="close-forgot-dialog"
                  className="w-full"
                >
                  Fermer
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={submitForgot} className="space-y-4">
              <div>
                <Label htmlFor="forgot-email" className="label-uppercase mb-1.5 block">
                  Adresse email
                </Label>
                <Input
                  id="forgot-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="prenom.nom@entreprise.fr"
                  data-testid="forgot-email-input"
                />
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setForgotOpen(false)}
                  data-testid="cancel-forgot-dialog"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={forgotLoading}
                  data-testid="submit-forgot-request"
                  className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
                >
                  {forgotLoading ? "Envoi…" : "Notifier l'administrateur"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
